import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { FileText, Eye, DownloadSimple, Pen, CheckCircle, Loader as LoaderIcon, UploadSimple, Trash } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  portalListConventions, portalSignConvention, portalPreviewConvention, portalDownloadConvention,
} from '../services/api';
import { SignaturePad } from '../components/SignaturePad';
import { formatApiError } from '../utils/apiError';

const CONTRACT_TYPE_LABELS = {
  CIPAESSE: 'Convention cadre C.I.P.A.E.S.S.E',
  CIPASCOP: 'Convention cadre C.I.P.A.S.C.O.P',
  APPORT_INDUSTRIE: 'Apport en industrie',
  AVENANT: 'Avenant',
  AUTRE: 'Convention',
};

const STATUS_LABELS = {
  draft: { label: 'Brouillon', color: 'bg-gray-100 text-gray-700' },
  validated: { label: 'Validée', color: 'bg-blue-100 text-blue-700' },
  signed: { label: 'Signée', color: 'bg-emerald-100 text-emerald-700' },
  archived: { label: 'Archivée', color: 'bg-[#F0EFEB] text-[#5E6D66]' },
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR');
}

export function PortalConventionsPage() {
  const [items, setItems] = useState([]);
  const [cooperator, setCooperator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewDialog, setViewDialog] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [signDialog, setSignDialog] = useState(null);
  const [signedBy, setSignedBy] = useState('');
  const [signatureImage, setSignatureImage] = useState(null);
  const [signMode, setSignMode] = useState('draw');
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await portalListConventions({ archived: false });
      setItems(r.data.items || []);
      setCooperator(r.data.cooperator);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // PDF preview
  useEffect(() => {
    let obj = null;
    async function load() {
      if (!viewDialog) { setPreviewUrl(null); return; }
      setPreviewLoading(true);
      try {
        const res = await portalPreviewConvention(viewDialog.id);
        obj = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        setPreviewUrl(obj);
      } catch { setPreviewUrl(null); }
      finally { setPreviewLoading(false); }
    }
    load();
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [viewDialog]);

  const onDownload = async (c) => {
    try {
      const res = await portalDownloadConvention(c.id);
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${c.contract_reference || 'convention'}.docx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Convention téléchargée');
    } catch { toast.error('Erreur téléchargement'); }
  };

  const openSign = (c) => {
    setSignedBy(cooperator ? `${cooperator.first_name || ''} ${cooperator.last_name || ''}`.trim() || cooperator.legal_name || '' : '');
    setSignatureImage(null);
    setSignMode('draw');
    setSignDialog(c);
  };

  const onPngUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Format PNG/JPG uniquement'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setSignatureImage(ev.target.result);
    reader.readAsDataURL(file);
  };

  const submitSign = async () => {
    if (!signedBy) { toast.error('Indiquez votre nom complet'); return; }
    if (!signatureImage) { toast.error('Signature graphique requise'); return; }
    setSaving(true);
    try {
      await portalSignConvention(signDialog.id, {
        signed_by: signedBy,
        signature_date: new Date().toISOString().slice(0, 10),
        signature_image: signatureImage,
      });
      toast.success('Convention signée en ligne');
      setSignDialog(null);
      setSignatureImage(null);
      fetchItems();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="portal-conventions-page">
      <div>
        <h1 className="text-2xl font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>Mes conventions</h1>
        <p className="text-sm text-[#5E6D66] mt-1">
          Consultez, téléchargez et signez en ligne vos conventions coopératives.
          {cooperator && (
            <span className="ml-1">Coopérateur : <span className="font-medium">{cooperator.numero_associe}</span></span>
          )}
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#2D5A4C]" /></div>
      ) : items.length === 0 ? (
        <Card className="border-[#E8E6E1]">
          <CardContent className="py-12 text-center">
            <FileText size={40} className="mx-auto text-[#E8E6E1] mb-3" />
            <p className="text-[#5E6D66]">Aucune convention à afficher pour le moment.</p>
            {!cooperator && (
              <p className="text-xs text-[#5E6D66] mt-2">Votre compte n'est pas encore rattaché à un coopérateur CMC. Contactez votre référent SCOP.</p>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="portal-conventions-list">
          {items.map((c) => {
            const st = STATUS_LABELS[c.status] || STATUS_LABELS.draft;
            const canSign = c.status === 'draft' || c.status === 'validated';
            return (
              <Card key={c.id} className="border-[#E8E6E1] hover:shadow-md transition-shadow" data-testid={`portal-conv-${c.id}`}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs text-[#5E6D66] font-mono">{c.contract_reference}</p>
                      <p className="text-sm font-semibold text-[#1A2421] mt-0.5">
                        {CONTRACT_TYPE_LABELS[c.contract_type] || c.title}
                      </p>
                    </div>
                    <Badge className={`text-[10px] ${st.color}`}>{st.label}</Badge>
                  </div>
                  <div className="text-xs text-[#5E6D66]">
                    Du {fmtDate(c.start_date)} au {fmtDate(c.end_date)}
                  </div>
                  <div className="flex flex-wrap gap-1 pt-2 border-t border-[#F0EFEB]">
                    <Button size="sm" variant="outline" onClick={() => setViewDialog(c)} data-testid={`portal-conv-view-${c.id}`}>
                      <Eye size={12} className="mr-1" /> Voir
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => onDownload(c)} data-testid={`portal-conv-download-${c.id}`}>
                      <DownloadSimple size={12} className="mr-1" /> DOCX
                    </Button>
                    {canSign && (
                      <Button size="sm" className="bg-[#2D5A4C] hover:bg-[#1E4136]" onClick={() => openSign(c)} data-testid={`portal-conv-sign-${c.id}`}>
                        <Pen size={12} className="mr-1" /> Signer
                      </Button>
                    )}
                    {c.status === 'signed' && (
                      <span className="text-xs text-emerald-600 flex items-center gap-1 ml-auto"><CheckCircle size={12} weight="fill" /> Signée</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* View Dialog */}
      <Dialog open={!!viewDialog} onOpenChange={(o) => !o && setViewDialog(null)}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="portal-conv-view-dialog">
          <DialogHeader>
            <DialogTitle>{viewDialog && (CONTRACT_TYPE_LABELS[viewDialog.contract_type] || viewDialog.title)}</DialogTitle>
            <DialogDescription>Réf. {viewDialog?.contract_reference}</DialogDescription>
          </DialogHeader>
          <div className="flex-1 border border-[#E8E6E1] rounded overflow-hidden bg-[#F9F8F5]">
            {previewLoading ? (
              <div className="h-full flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#2D5A4C]" /></div>
            ) : previewUrl ? (
              <iframe src={previewUrl} title="Aperçu convention" className="w-full h-full min-h-[500px]" data-testid="portal-conv-view-iframe" />
            ) : (
              <div className="h-full flex items-center justify-center text-xs text-[#5E6D66]">Aperçu indisponible</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onDownload(viewDialog)}><DownloadSimple size={14} className="mr-1" /> Télécharger DOCX</Button>
            <Button onClick={() => setViewDialog(null)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sign Dialog */}
      <Dialog open={!!signDialog} onOpenChange={(o) => { if (!o) { setSignDialog(null); setSignatureImage(null); } }}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="portal-conv-sign-dialog">
          <DialogHeader>
            <DialogTitle>Signer en ligne</DialogTitle>
            <DialogDescription>Réf. {signDialog?.contract_reference}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Signataire (nom complet) *</Label>
              <Input value={signedBy} onChange={(e) => setSignedBy(e.target.value)} data-testid="portal-conv-sign-signed-by" />
            </div>
            <div>
              <Label className="text-sm font-semibold">Signature graphique *</Label>
              <div className="flex gap-1 my-2">
                <Button size="sm" variant={signMode === 'draw' ? 'default' : 'outline'} onClick={() => { setSignMode('draw'); setSignatureImage(null); }} className={signMode === 'draw' ? 'bg-[#2D5A4C] hover:bg-[#1E4136]' : ''}>
                  <Pen size={12} className="mr-1" /> Tracer
                </Button>
                <Button size="sm" variant={signMode === 'upload' ? 'default' : 'outline'} onClick={() => { setSignMode('upload'); setSignatureImage(null); }} className={signMode === 'upload' ? 'bg-[#2D5A4C] hover:bg-[#1E4136]' : ''}>
                  <UploadSimple size={12} className="mr-1" /> PNG
                </Button>
              </div>
              {signMode === 'draw' && !signatureImage && (
                <SignaturePad onSave={(url) => { setSignatureImage(url); toast.success('Signature capturée'); }} width={500} height={160} />
              )}
              {signMode === 'upload' && !signatureImage && (
                <div className="border-2 border-dashed border-[#E8E6E1] rounded-lg p-6 text-center">
                  <input type="file" accept="image/png,image/jpeg" onChange={onPngUpload} className="hidden" id="portal-sign-upload" data-testid="portal-conv-sign-upload" />
                  <label htmlFor="portal-sign-upload" className="cursor-pointer">
                    <UploadSimple size={28} className="mx-auto text-[#5E6D66] mb-2" />
                    <p className="text-xs text-[#5E6D66]">Cliquez pour importer une signature PNG/JPG</p>
                  </label>
                </div>
              )}
              {signatureImage && (
                <div className="space-y-2">
                  <div className="border border-[#E8E6E1] rounded-lg p-2 bg-white flex items-center justify-center">
                    <img src={signatureImage} alt="Signature" className="max-h-[120px] max-w-full" />
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setSignatureImage(null)} data-testid="portal-conv-sign-clear">
                    <Trash size={12} className="mr-1" /> Effacer
                  </Button>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSignDialog(null); setSignatureImage(null); }}>Annuler</Button>
            <Button onClick={submitSign} disabled={saving || !signatureImage} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="portal-conv-sign-confirm">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirmer la signature'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
